
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCountry } from '@/contexts/country';

export interface Insight {
  id: string;
  title: string;
  slug: string;
  content: string;
  published: boolean;
  created_at: string;
  category_id: string;
  country_code: string;
  agree_percentage: number; 
  total_pollsters: number;  
  linked_poll: string | null;
  stats_status: string | null;
  category: {
    id: string;
    name: string;
    slug: string;
  };
}

export const useFilteredInsights = (
  categoryId?: string | null,
  countryCode?: string | null,
  limit?: number
) => {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { countryCode: contextCountryCode } = useCountry();
  
  useEffect(() => {
    // Use provided countryCode first, then fallback to context's countryCode
    const effectiveCountryCode = countryCode || contextCountryCode || null;
    
    async function fetchInsights() {
      setIsLoading(true);
      setError(null);
      // Clear existing data immediately when country changes to prevent cross-country leak
      setInsights([]);
      
      try {
        // Query insight_cards table with proper joins for category
        let query = supabase
          .from('insight_cards')
          .select(`
            id, 
            title, 
            slug, 
            description,
            status,
            created_at,
            category_id,
            country_code,
            agree_percentage,
            total_pollsters,
            category:insight_categories(id, name, slug)
          `)
          .eq('status', 'published')
          .order('created_at', { ascending: false });
        
        // Fetch poll-card link information
        const { data: linksData } = await supabase
          .from('poll_card_links')
          .select('card_id, poll_id');
        
        // Create a map of card_id to poll_id for quick lookup
        const cardPollMap = new Map();
        if (linksData) {
          linksData.forEach(link => {
            cardPollMap.set(link.card_id, link.poll_id);
          });
        }
        
        // Apply country filter if provided
        if (effectiveCountryCode) {
          query = query.eq('country_code', effectiveCountryCode);
        }
        
        // Apply category filter if provided
        if (categoryId) {
          query = query.eq('category_id', categoryId);
        }
        
        // Apply limit if provided
        if (limit && limit > 0) {
          query = query.limit(limit);
        }
        
        const { data, error: fetchError } = await query;
        
        if (fetchError) throw fetchError;
        
        // Transform the data to match the Insight interface
        if (data) {
          const transformedInsights: Insight[] = data.map(item => ({
            id: item.id,
            title: item.title,
            slug: item.slug || '',
            content: item.description || '',
            published: item.status === 'published',
            created_at: item.created_at,
            category_id: item.category_id || '',
            country_code: item.country_code || '',
            agree_percentage: item.agree_percentage || 0,
            total_pollsters: item.total_pollsters || 0,
            linked_poll: cardPollMap.get(item.id) || null,
            stats_status: 'pending',
            category: item.category || { id: '', name: '', slug: '' }
          }));
          
          console.log(`Fetched ${transformedInsights.length} insights for country: ${effectiveCountryCode}`);
          
          setInsights(transformedInsights);
        } else {
          setInsights([]);
        }
      } catch (err) {
        console.error('Error fetching insights:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch insights'));
        setInsights([]);
      } finally {
        setIsLoading(false);
      }
    }
    
    fetchInsights();
  }, [countryCode, categoryId, limit, contextCountryCode]);
  
  return { insights, isLoading, error };
};
